import React, { Suspense, useEffect } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { PageSkeleton } from "../../../components/loading/PageSkeleton";
import { CoachClientsFilter } from "../../../components/coach-clients/CoachClientsFilter";
import { useQueryParam } from "../../../hooks/useQueryParam";
import { ClientSort } from "../../../constants";
import { toEnum } from "../../../utils/misc";
import { CoachClientFiltersContext } from "../../../contexts/CoachClientFiltersContext";
import { Container } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { polyfillCSS } from "../../../utils/css";
import {
  COACH_CLIENTS_ACTIVE_ROUTE,
  COACH_CLIENTS_FORMS_ROUTE,
  COACH_CLIENTS_TAGS_ROUTE,
} from "../../../routes/routes";
import CoachClientListContext from "../../../contexts/CoachClientListContext";
import {
  ClientsCountDto,
  ClientsVm,
  TagDto,
  UserInviteStatus,
} from "@growth-machine-llc/stridist-api-client";
import { useQuery } from "@tanstack/react-query";
import TagsService from "../../../services/TagsService";
import { useCoachTabsContext } from "../../../contexts/CoachTabsContext";
import ClientsService from "../../../services/ClientsService";

const useStyles = makeStyles((theme) => ({
  root: {
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(11),
    paddingLeft: polyfillCSS(
      `calc(${theme.spacing(4)} + var(--safe-area-inset-left)) !important`,
    ),
    paddingRight: polyfillCSS(
      `calc(${theme.spacing(10)} + var(--safe-area-inset-right)) !important`,
    ),
    [theme.breakpoints.down("lg")]: {
      paddingLeft: "20px !important",
      paddingRight: "20px !important",
    },
  },
  filters: {
    marginBottom: theme.spacing(2),
  },
}));

export const TAGS_QUERY_KEY = "tags";
export const COACH_PENDING_CLIENTS_COUNT_QUERY_KEY = "coach-clients-count";
export const ALL_OPTIONS = "ALL" as const;
export type ALL_OPTIONS_TYPE = typeof ALL_OPTIONS;

const CoachClientsWrapper = () => {
  const s = useStyles();
  const location = useLocation();

  const [coachClientsList, setCoachClientsList] = React.useState<ClientsVm>();
  const coachClientListContextValue = { coachClientsList, setCoachClientsList };

  const { data: tagList } = useQuery({
    queryKey: [TAGS_QUERY_KEY],
    queryFn: TagsService.getAllTags,
  });

  const { setClientsCount } = useCoachTabsContext();

  const { data: pendingClientsCount } = useQuery({
    queryKey: [COACH_PENDING_CLIENTS_COUNT_QUERY_KEY],
    // NOTE: Fetching only `PENDING` and `ADDED` clients since only this count is displayed in the tabs
    queryFn: () =>
      ClientsService.getClientsCount([
        UserInviteStatus.ADDED,
        UserInviteStatus.PENDING,
      ]),
  });

  useEffect(() => {
    pendingClientsCount &&
      setClientsCount({
        pending: pendingClientsCount.count,
      });
  }, [pendingClientsCount?.count]);

  const defaultFilters = {
    query: "",
    sortKey: ClientSort.NAME_ASC,
  };

  const [page, setPage] = React.useState<number>(1);
  const [query, setQuery] = React.useState(defaultFilters.query);
  const [tagIds, setFilterTags] = React.useState<number[] | ALL_OPTIONS_TYPE>(
    ALL_OPTIONS,
  );

  const [sortKey, setSortKey] = useQueryParam<ClientSort>(
    "sort",
    defaultFilters.sortKey,
    {
      silent: true,
      normalize: (param) => toEnum(param, ClientSort, ClientSort.NAME_ASC),
    },
  );

  const isClientsTagsRoute = location.pathname.includes(
    COACH_CLIENTS_TAGS_ROUTE,
  );

  useEffect(() => {
    setQuery("");
  }, [location.pathname]);

  const handleQueryChange = React.useCallback(
    (query: string) => {
      setQuery(query);
      setPage(1);
    },
    [setQuery],
  );

  const handleSortKeyChange = React.useCallback(
    (sortKey: ClientSort) => {
      setSortKey(sortKey);
    },
    [setSortKey],
  );

  const handleFilterTagsIdChange = React.useCallback(
    (tagIds?: number[], allOptionsSelected?: boolean) => {
      setFilterTags(allOptionsSelected ? ALL_OPTIONS : tagIds);
    },
    [setFilterTags],
  );

  const contextValue = {
    page,
    setPage,
    query,
    setQuery,
    filterTagIds: tagIds,
    setFilterTagIdsChange: setFilterTags,
    tagList,
    sortKey,
    setSortKey,
  };

  return (
    <>
      <CoachClientFiltersContext.Provider value={contextValue}>
        <CoachClientListContext.Provider value={coachClientListContextValue}>
          {location.pathname === COACH_CLIENTS_FORMS_ROUTE ? (
            <Outlet />
          ) : (
            <Container className={s.root} maxWidth="xl">
              <CoachClientsFilter
                className={s.filters}
                query={query}
                onChangeQuery={handleQueryChange}
                sortKey={sortKey}
                onChangeSortKey={handleSortKeyChange}
                tagList={tagList || []}
                onChangeFilterTagId={handleFilterTagsIdChange}
                filterTagIds={tagIds}
                isClientsTagsRoute={isClientsTagsRoute}
                defaultValues={defaultFilters}
                disabled={
                  !location.pathname.includes(COACH_CLIENTS_ACTIVE_ROUTE)
                }
              />
              <Outlet />
            </Container>
          )}
        </CoachClientListContext.Provider>
      </CoachClientFiltersContext.Provider>
    </>
  );
};

export default CoachClientsWrapper;
